// useAuthStore2.tsx
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jwtDecode } from "jwt-decode";

const API_BASE = import.meta.env.VITE_AUTHURL;

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;
  customerId: number | null;
  isConfirmed: boolean;
  userId: number | null;
  appMode: string | null;
  userName: string | null;
  appRole: string | null;
  lang: string | null;

  login: (email: string, password: string) => Promise<any>;
  signup: (data: any) => Promise<any>;
  logout: () => void;
  refresh: () => Promise<void>;
  resetPassword: (email: string) => Promise<any>;
  updateCustomer: (customerId: number) => Promise<any>;
  hasRole: (role: string) => boolean;
}

async function apiRequest(
  endpoint: string,
  body: object,
  token?: string | null,
  onLoadingChange?: (loading: boolean) => void,
  onError?: (msg: string) => void,
): Promise<any> {
  onLoadingChange?.(true);
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "Authorization": `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.detail || "Request failed");
    }

    return await response.json();
  } catch (err: any) {
    onError?.(err.message);
    throw err;
  } finally {
    onLoadingChange?.(false);
  }
}

export const useAuthStore = create<AuthState>((set, get) => {
  // Хелперы для передачи в apiRequest
  const setLoading = (loading: boolean) => set({ isLoading: loading });
  const setError = (msg: string) => set({ error: msg });

  return {
    accessToken: null,
    refreshToken: null,
    isLoading: false,
    error: null,
    customerId: null,
    isConfirmed: false,
    userId: null,
    appMode: null,
    userName: null,
    appRole: null,
    lang: null,

    login: async (email, password) => {
      set({ error: null });
      const data = await apiRequest(
        "/login/",
        { username: email, password },
        null,
        setLoading,
        setError,
      );

      console.log("STORE: Login success, token set:", data.access.substring(0, 10) + "...");
      const decoded: any = jwtDecode(data.access);

      set({
        accessToken: data.access,
        refreshToken: data.refresh,
        customerId: decoded.customer_id,
        isConfirmed: decoded.is_email_confirmed,
        // isConfirmed: decoded.is_confirmed,
        userId: decoded.user_id,
        appMode: decoded.app_mode,
        userName: decoded.username,
        appRole: decoded.app_role,
        lang: decoded.lang || 'uk',
      });

      return data;
    },

    signup: async (data) => {
      return await apiRequest("/registration/lpr/", data, null, setLoading, setError);
    },

    logout: () => {
      set({
        accessToken: null,
        refreshToken: null,
        customerId: null,
        isConfirmed: false,
        userId: null,
        appMode: null,
        userName: null,
        appRole: null,
        lang: null,
      });
      window.location.href = "/login";
    },

    refresh: async () => {
      const data = await apiRequest(
        "/token/refresh/",
        { refresh: get().refreshToken },
        null,
        setLoading,
        setError,
      );
      console.log('refresh new accessToken', data.access);
      // set({ accessToken: data.access });
      // todo: обновлять остальные claims если роль/язык могут измениться

      set((state) => ({ ...state, accessToken: data.access }))
    },

    resetPassword: async (email) => {
      return await apiRequest("/auth/password/reset/", { email }, null, setLoading, setError);
    },

    updateCustomer: async (customerId) => {
      return await apiRequest(
        "/dj_user/",
        { customer_id: customerId },
        get().accessToken,
        setLoading,
        setError,
      );
    },

    hasRole: (role) => {
      const roles = get().appRole?.split(",") || [];
      return roles.includes(role);
    },
  };
});